import React, {useCallback, useEffect, useState} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {setNestedObjectValues} from 'formik';
import {useSnackbar} from 'notistack';
import {captureEvent} from '@sentry/react';
import useSaveRecommendation from '../../useSaveRecommendation/useSaveRecommendation';
import {useAccountContext} from '../../../../contexts/AccountContext';
import {recommendFieldsType} from '../recommendFields';
import {
  finaliseRecommendFactory,
  saveDraftRecommendFactory,
  setContactnameFromGoals,
} from './utils';
import UnfinishedFormPrompt from '../../../../components/Common/UnfinishedFormPrompt';
import {useRecommendContext} from '../CreateRecommendHelpers';
import {FixedFooter} from '../../../../components/Layout/Layout';
import {OvrecommendDetails_ovrecommendDetails} from '../../../../schema-types/OvrecommendDetails';
import {
  BusinessUnitEnum,
  GlobalPermissionEnum,
  OVRecommendActionEnum,
} from '../../../../schema-types/globalTypes';
import {SPP_RECOMMENDATION_TYPES} from '../../../../constants/SolutionType';
import {
  RequirementPots_destinations,
  RequirementPots_sources,
} from '../../../../schema-types/RequirementPots';
import {
  useAutoForm,
  useLoadedAutoField,
} from '../../../../components/AutoForm/useAutoForm';
import {ScrollableModal} from '../../../../components/Common/ScrollableModal';
import {useDjangoContext} from '../../../../contexts/useAuthContext';
import {useGetSppReview} from '../../../../pages/AccountInstructionsPage/AccountInstructionsPage';

export const reccNonEditable = (
  recommendDetails: OvrecommendDetails_ovrecommendDetails | null
) => {
  return recommendDetails?.recommends?.some((x) => !!x.curoId);
};

const getReccWarning = (
  values: recommendFieldsType
): {error: string | null; allowToSave: boolean} => {
  if (values.mainPlanType === 'pension' || values.forcedMainPlanType === 'pension') {
    if (values.sppType === SPP_RECOMMENDATION_TYPES.ExecutionOnly) {
      return {
        error: `The Saltus SIPP is currently an advised product only. You have selected execution only.`,
        allowToSave: false,
      };
    }
  }
  if (
    values.planAction === OVRecommendActionEnum.TRANSFER &&
    (values.mainPlanType === 'pension' || values.forcedMainPlanType === 'pension')
  ) {
    return {
      error: `Don't forget to add instructions for any existing regular withdrawals/contributions that the client wishes to continue following the transfer`,
      allowToSave: true,
    };
  }
  return {error: null, allowToSave: true};
};

export const CreateRecommendFooter: React.FC<{
  destinationPots: RequirementPots_destinations[];
  sourcePots: RequirementPots_sources[];
}> = ({destinationPots, sourcePots}) => {
  const {perms} = useDjangoContext();
  const {values, dirty, validateForm, isValid, setTouched, touched} =
    useAutoForm<recommendFieldsType>();
  const [savingDraft, setSavingDraft] = useState(false);

  const {loaded} = useLoadedAutoField();
  const [warning, setWarning] = useState<{error: string | null; allowToSave: boolean}>({
    allowToSave: true,
    error: null,
  });
  const [finalising, setFinalising] = useState(false);
  const [contactName, setContactName] = useState<string>('');
  // const {accountContactsLoading, accountContactsData, accountContactsError} =
  //   useAccountContacts();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {account, accountId, accountLoading, accountType, potsLoading} =
    useAccountContext();
  const isSppDA = accountType === BusinessUnitEnum.DA;
  const {refetch} = useGetSppReview(accountId!, {skip: !isSppDA});
  const {recommendDetails, recommendReviewId, loadingOvRecommend} = useRecommendContext();
  const {createUpdate} = useSaveRecommendation(setFinalising, setSavingDraft, values.how);
  const saveDraftRecommend = saveDraftRecommendFactory({
    setSavingDraft,
    createUpdate,
    values,
    contactName,
    sources: sourcePots!,
    destinations: destinationPots,
  });
  const closeWarning = useCallback(
    () => setWarning({allowToSave: true, error: null}),
    []
  );
  const finaliseRecommend = finaliseRecommendFactory({
    createUpdate,
    closeWarning,
    values,
    contactName,
    sources: sourcePots!,
    destinations: destinationPots,
    setFinalising,
    captureEvent,
    closeSnackbar,
    validateForm,
    setNestedObjectValues,
    setTouched,
    enqueueSnackbar,
    refetch,
  });

  // useEffect(() => {
  //   updatePlanTypeContainerSources({
  //     values,
  //     getSourcesAndDestinations,
  //     account,
  //     accountId,
  //     recommendReviewId,
  //   });
  // }, [values.planAction, values.mainPlanType, values.contact]);

  useEffect(() => {
    if (account) {
      setContactnameFromGoals({
        values,
        account,
        setContactName,
      });
    }
  }, [account, values.contact]);

  const checkWarningAndFinalize = (skipChecks: boolean) => {
    if (skipChecks && perms.includes(GlobalPermissionEnum.can_access_admin_features)) {
      return finaliseRecommend();
    }
    const newWarning = getReccWarning(values);
    if (newWarning.error) {
      setWarning(newWarning);
    } else {
      finaliseRecommend();
    }
  };
  const disableSaving = reccNonEditable(recommendDetails) || !loaded;
  const submitFirstWord = recommendDetails?.id ? 'Update' : 'Add';
  const finaliseButtonText = `${submitFirstWord} ${values.how}`;

  const disableEverything = potsLoading || accountLoading || loadingOvRecommend;

  return (
    <>
      <ScrollableModal
        className="modal"
        open={!!warning.error}
        onClose={() => closeWarning()}
      >
        <Box height="100%" display="flex" alignItems="center" justifyContent="center">
          <Box p={4} borderRadius="8px" bgcolor="white">
            <Typography variant="h6">{warning.error}</Typography>
            <Box mt={2} />
            <Box display="flex" justifyContent="space-between">
              <Button color="primary" variant="contained" onClick={() => closeWarning()}>
                Close popup
              </Button>
              <Box />
              {warning.allowToSave ? (
                <Button
                  data-test="Finalise"
                  color="primary"
                  variant="contained"
                  onClick={finaliseRecommend}
                >
                  Continue
                </Button>
              ) : null}
            </Box>
          </Box>
        </Box>
      </ScrollableModal>
      <ScrollableModal size="small" className="modal" open={savingDraft || finalising}>
        <Box
          flex={1}
          className="saving"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography fontSize={16}>
            {savingDraft || finalising ? `Saving ${values.how}...` : null}
          </Typography>
        </Box>
      </ScrollableModal>
      <Box width="100%" pb={12} />
      <FixedFooter>
        {disableSaving && recommendDetails?.review?.dateSubmitted ? (
          <Box mr={1} display="flex" justifyContent="flex-end">
            <Typography color="primary">Already submitted, cannot re-submit</Typography>
            <Box />
          </Box>
        ) : null}
        <Box mt={1} display="flex" justifyContent="flex-end">
          <Box mr={1}>
            <UnfinishedFormPrompt
              ariaLabel="Requirements"
              path="../requirements"
              dirty={dirty}
            />
          </Box>
          {values.planAction && (
            <>
              <Box mr={1} display="flex" gap={2} alignContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    savingDraft || finalising || disableSaving || disableEverything
                  }
                  onClick={saveDraftRecommend}
                >
                  {savingDraft ? 'Saving...' : 'Save As Draft'}
                </Button>
              </Box>
              <Box>
                <Button
                  data-test="Finalise"
                  variant="contained"
                  color="primary"
                  disabled={
                    savingDraft || finalising || disableSaving || disableEverything
                  }
                  onClick={(e) => checkWarningAndFinalize(e.altKey)}
                >
                  {finalising ? 'Saving...' : finaliseButtonText}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </FixedFooter>
    </>
  );
};
